import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Card, Badge, Button, EmptyState, Checkbox, Modal } from '@opendate-app/setlist';
import client from '../../../shared/api/client';
import { toastError, errorText } from '../../../shared/toastHelper';
import DestructiveConfirmModal from '../../../shared/DestructiveConfirmModal';
import DisputeEvidencePane from '../../DisputeEvidence/components/DisputeEvidencePane';
import '../styles/riskDesk.css';

const CONFIRMS = {
  block_card: {
    title: 'Block this card?',
    confirmLabel: 'Block card',
    icon: 'far fa-ban',
    body: (row, teamScoped) => (
      teamScoped ? (
        <p>This adds the card&apos;s fingerprint to your Opendate Radar block list, so future charges on that card are declined at checkout.</p>
      ) : (
        <>
          <p>This adds the card&apos;s fingerprint to the Opendate Radar block list of <strong>every venue the ring touched</strong>, so future charges on that card are declined at checkout.</p>
          <p>The ring is marked <strong>actioned</strong> and leaves the queue — it won&apos;t alert again.</p>
        </>
      )
    ),
  },
  block_domain: {
    title: 'Block this domain?',
    confirmLabel: 'Block domain',
    icon: 'far fa-ban',
    body: (row) => (
      <>
        <p>This creates a <strong>block rule</strong> for the domain so the next scan flags any order from it as severe and adds the cluster&apos;s buyer emails to Opendate Radar.</p>
        {row.domainSiblings && row.domainSiblings.sameTeam > 0 ? (
          <p>It also clears the <strong>{row.domainSiblings.sameTeam} other open {row.domainSiblings.sameTeam === 1 ? 'row' : 'rows'}</strong> for this domain on this operator — the block rule already covers them.</p>
        ) : null}
      </>
    ),
  },
  dismiss: {
    title: 'Dismiss this row?',
    confirmLabel: 'Dismiss',
    icon: 'far fa-times-circle',
    body: () => (
      <>
        <p>This clears the row from the queue and marks it <strong>dismissed</strong>. It won&apos;t be resurrected on future scans.</p>
        <p><strong>Nothing is blocked</strong> — use this when it&apos;s a false positive.</p>
      </>
    ),
  },
};

const FLAG_OPTIONS = [
  ['dispute', 'Dispute'],
  ['early_fraud_warning', 'Issuer alert'],
  ['in_review', 'In review'],
  ['radar_review', 'Radar review'],
  ['opendate_rule', 'Opendate rule'],
  ['attack_ring', 'Attack ring'],
  ['domain_cluster', 'Domain cluster'],
  ['gift_card_chargeback', 'Gift card chargeback'],
];

const EMPTY_FILTERS = { flag: 'all', acts: 'all', clock: 'all', exposure: 'all', team: '' };

const actsBucket = (row) => {
  if (row.serviceLevel == null) return 'across';
  if (row.serviceLevel === 'self_service') return 'self_service';
  return 'managed';
};

const matchesClock = (row, bucket) => {
  const isDispute = row.kind === 'dispute';
  const { deadlineHours: h, daysQuiet: d } = row;
  switch (bucket) {
    case 'due_24h': return isDispute && h != null && h >= 0 && h < 24;
    case 'due_72h': return isDispute && h != null && h >= 0 && h < 72;
    case 'closed': return isDispute && h != null && h < 0;
    case 'quiet_14d': return !isDispute && d != null && d >= 14;
    default: return true;
  }
};

const FilterSelect = ({ name, value, onChange, options }) => (
  <label className="risk-desk-filter">
    <span className="risk-desk-filter-name">{name}</span>
    <select className="risk-desk-filter-select" value={value} onChange={onChange} aria-label={name}>
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  </label>
);

FilterSelect.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.array).isRequired,
};

const DomainSweep = ({ siblings, checked, scope, onToggle, onScope }) => {
  const spansTeams = siblings.allTeams > siblings.sameTeam;
  const noun = (n) => (n === 1 ? 'row' : 'rows');
  const effectiveCount = scope === 'all' ? siblings.allTeams : siblings.sameTeam;
  return (
    <div className="risk-desk-sweep">
      <Checkbox
        label={`Also dismiss the ${effectiveCount} other open ${noun(effectiveCount)} using this domain`}
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
      />
      {checked && spansTeams ? (
        <div className="risk-desk-sweep-scope">
          <label>
            <input type="radio" name="risk-desk-sweep-scope" checked={scope === 'team'} onChange={() => onScope('team')} />
            {` This operator only (${siblings.sameTeam})`}
          </label>
          <label>
            <input type="radio" name="risk-desk-sweep-scope" checked={scope === 'all'} onChange={() => onScope('all')} />
            {` All operators (${siblings.allTeams})`}
          </label>
        </div>
      ) : null}
    </div>
  );
};

DomainSweep.propTypes = {
  siblings: PropTypes.shape({ sameTeam: PropTypes.number, allTeams: PropTypes.number }).isRequired,
  checked: PropTypes.bool.isRequired,
  scope: PropTypes.oneOf(['team', 'all']).isRequired,
  onToggle: PropTypes.func.isRequired,
  onScope: PropTypes.func.isRequired,
};

const RiskDesk = ({ rows, endpoints, currencyCode, teamScoped, canAct, bare }) => {
  const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode });
  const wholeMoney = new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode, maximumFractionDigits: 0 });

  const [busyKey, setBusyKey] = useState(null);
  const [confirmKey, setConfirmKey] = useState(null);
  const [fraudulent, setFraudulent] = useState(true);
  const [blockList, setBlockList] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pending, setPending] = useState(null);
  const [applyDomain, setApplyDomain] = useState(false);
  const [applyScope, setApplyScope] = useState('team');
  const [disputeReview, setDisputeReview] = useState(null);
  const [disputeSubmitted, setDisputeSubmitted] = useState(false);
  const [netVenues, setNetVenues] = useState(null);
  const netRequestRef = useRef(0);

  const openNetworkVenues = async (row) => {
    const token = netRequestRef.current + 1;
    netRequestRef.current = token;
    setNetVenues({ loading: true, venues: [], error: null });
    try {
      const { data } = await client.get(`${endpoints.network_venues}?type=${row.type}&id=${row.id}`);
      if (netRequestRef.current === token) setNetVenues({ loading: false, venues: data.venues || [], error: null });
    } catch (e) {
      if (netRequestRef.current !== token) return;
      const message = errorText(e);
      toastError(message);
      setNetVenues({ loading: false, venues: [], error: message });
    }
  };

  const closeNetworkVenues = () => {
    netRequestRef.current += 1;
    setNetVenues(null);
  };

  const [clusterOrders, setClusterOrders] = useState(null);
  const clusterRequestRef = useRef(0);

  const openClusterOrders = async (row) => {
    const token = clusterRequestRef.current + 1;
    clusterRequestRef.current = token;
    setClusterOrders({ loading: true, orders: [], error: null, title: row.subject });
    try {
      const { data } = await client.get(`${endpoints.cluster_orders}?id=${row.id}`);
      if (clusterRequestRef.current === token) setClusterOrders({ loading: false, orders: data.orders || [], error: null, title: row.subject });
    } catch (e) {
      if (clusterRequestRef.current !== token) return;
      const message = errorText(e);
      toastError(message);
      setClusterOrders({ loading: false, orders: [], error: message, title: row.subject });
    }
  };

  const closeClusterOrders = () => {
    clusterRequestRef.current += 1;
    setClusterOrders(null);
  };

  const [orderModal, setOrderModal] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);

  const openOrder = (buyer) => {
    setOrderLoading(true);
    setOrderModal(buyer);
  };

  const openDisputeReview = (row) => {
    setDisputeSubmitted(false);
    setDisputeReview(row);
  };

  const closeDisputeReview = () => {
    const shouldReload = disputeSubmitted;
    setDisputeReview(null);
    setDisputeSubmitted(false);
    if (shouldReload) {
      window.location.reload();
    }
  };

  const openRefund = (row) => {
    setConfirmKey(row.key);
    setFraudulent(true);
    setBlockList(false);
  };

  const post = async (row, endpoint, body) => {
    setBusyKey(row.key);
    try {
      await client.post(endpoint, body);
      window.location.reload();
    } catch (e) {
      toastError(errorText(e));
      setBusyKey(null);
    }
  };

  const approve = (row) => post(row, endpoints.approve, { id: row.id });

  const refund = (row) => {
    setConfirmKey(null);
    post(row, endpoints.refund, { id: row.id, fraudulent, block_list: blockList });
  };

  const blockCard = (row) => setPending({ row, kind: 'block_card' });
  const blockDomain = (row) => setPending({ row, kind: 'block_domain' });
  const dismiss = (row) => {
    setApplyDomain(false);
    setApplyScope(row.domainSiblings && row.domainSiblings.sameTeam === 0 ? 'all' : 'team');
    setPending({ row, kind: 'dismiss' });
  };

  const runPending = () => {
    const { row, kind } = pending;
    if (kind === 'block_card') post(row, endpoints.block_card, { id: row.id });
    if (kind === 'block_domain') post(row, endpoints.block_domain, { id: row.id });
    if (kind === 'dismiss') {
      const body = { id: row.id, type: row.type };
      if (applyDomain) { body.apply_to_domain = true; body.scope = applyScope; }
      post(row, endpoints.dismiss, body);
    }
  };

  const TableWrap = bare ? 'div' : Card;
  const wrapProps = bare ? {} : { padding: 'none' };

  if (!rows.length) {
    if (bare) return null;
    return (
      <Card padding="md">
        <EmptyState
          size="sm"
          title={teamScoped ? 'No fraud rings or clusters' : 'The queue is clear'}
          description={teamScoped ? 'No attack rings or domain clusters are affecting your team right now.' : 'No reviews or disputes need attention right now.'}
        />
      </Card>
    );
  }

  const setFilter = (key) => (e) => {
    const { value } = e.target;
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const matchesFilters = (row) => {
    if (filters.flag !== 'all' && row.kind !== filters.flag) return false;
    if (filters.acts !== 'all' && actsBucket(row) !== filters.acts) return false;
    if (filters.clock !== 'all' && !matchesClock(row, filters.clock)) return false;
    if (filters.exposure !== 'all' && (row.dollars || 0) < Number(filters.exposure)) return false;
    const query = filters.team.trim().toLowerCase();
    if (query && !(row.teamName || '').toLowerCase().includes(query)) return false;
    return true;
  };

  const ranked = rows.map((row, index) => ({ row, rank: index + 1 }));
  const visible = teamScoped ? ranked : ranked.filter(({ row }) => matchesFilters(row));

  const atRisk = visible.reduce((sum, { row }) => sum + (row.expectedCost || 0), 0);
  const compactMoney = new Intl.NumberFormat('en-US', {
    style: 'currency', currency: currencyCode, notation: 'compact', maximumFractionDigits: 1,
  });

  return (
    <>
      {teamScoped ? (
        <div className="risk-desk-heading">
          <h3 className="risk-desk-heading-title">Fraud rings &amp; clusters</h3>
          <p className="risk-desk-heading-desc">Attack rings (one card disputing across multiple venues) and domain clusters (bulk signups from a single email domain) affecting your venue. Review each, then block or dismiss.</p>
        </div>
      ) : null}
      {!teamScoped ? (
        <div className="risk-desk-toolbar">
          <div className="risk-desk-summary">
            Showing {visible.length} of {ranked.length} {ranked.length === 1 ? 'case' : 'cases'} · {compactMoney.format(atRisk)} at risk
          </div>
          <div className="risk-desk-filters">
            <FilterSelect name="Flag" value={filters.flag} onChange={setFilter('flag')} options={[['all', 'Any'], ...FLAG_OPTIONS]} />
            <FilterSelect name="Acts" value={filters.acts} onChange={setFilter('acts')} options={[['all', 'Any'], ['self_service', 'Self-service'], ['managed', 'Managed'], ['across', 'Across Opendate']]} />
            <FilterSelect name="Clock" value={filters.clock} onChange={setFilter('clock')} options={[['all', 'Any'], ['due_24h', 'Due < 24h'], ['due_72h', 'Due < 72h'], ['closed', 'Window closed'], ['quiet_14d', 'Quiet 14d+']]} />
            <FilterSelect name="Exposure" value={filters.exposure} onChange={setFilter('exposure')} options={[['all', 'Any'], ['500', '≥ $500'], ['5000', '≥ $5k'], ['25000', '≥ $25k']]} />
            <input type="search" className="risk-desk-search" placeholder="Search team" aria-label="Search team" value={filters.team} onChange={setFilter('team')} />
          </div>
        </div>
      ) : null}
      {visible.length === 0 ? (
        <Card padding="md">
          <EmptyState
            size="sm"
            title="No matches"
            description="No rows match this filter."
          />
        </Card>
      ) : (
      <TableWrap {...wrapProps}>
        <table className="risk-desk-table">
          <thead>
            <tr>
              <th scope="col" className="risk-desk-right risk-desk-num">#</th>
              <th scope="col">{teamScoped ? 'Card / domain' : 'Team'}</th>
              <th scope="col">Flag</th>
              {!teamScoped ? <th scope="col">Acts</th> : null}
              <th scope="col">Total Risk</th>
              <th scope="col">Expected Loss</th>
              <th scope="col">Clock</th>
              <th scope="col" className="risk-desk-right" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {visible.map(({ row, rank }) => {
              const busy = busyKey === row.key;
              const confirming = confirmKey === row.key;
              const isCluster = row.type === 'cluster';
              const detailLines = (teamScoped
                ? [row.subtitle]
                : [
                  row.subject && row.subject !== row.teamName ? row.subject : null,
                  row.cardLabel || null,
                  isCluster ? null : row.subtitle,
                  row.disputeReason ? `Reason: ${row.disputeReason}` : null,
                  row.flagReason || null,
                ]).filter(Boolean);
              return (
                <tr key={row.key} data-testid={`risk-row-${row.key}`}>
                  <td className="risk-desk-right risk-desk-num-cell">{rank}</td>
                  <td>
                    <div className="risk-desk-team">{teamScoped ? row.subject : row.teamName}</div>
                    {detailLines.map((line, i) => (
                      <div key={i} className="risk-desk-network">{line}</div>
                    ))}
                    {!teamScoped && isCluster && row.subtitle ? (
                      <div className="risk-desk-network">
                        <button type="button" className="risk-desk-linklike" onClick={() => openClusterOrders(row)}>{row.subtitle}</button>
                      </div>
                    ) : null}
                    {!teamScoped && row.buyer ? (
                      <div className="risk-desk-network">
                        {[row.buyer.name, row.buyer.email].filter(Boolean).map((part) => `${part} · `).join('')}
                        <button type="button" className="risk-desk-linklike" onClick={() => openOrder(row.buyer)}>{`order #${row.buyer.orderId}`}</button>
                      </div>
                    ) : null}
                    {!teamScoped && row.networkSpread ? (
                      <div className="risk-desk-network">
                        {`Across Opendate — ${row.networkSpread.events} ${row.networkSpread.events === 1 ? 'event' : 'events'} at `}
                        <button type="button" className="risk-desk-linklike" onClick={() => openNetworkVenues(row)}>
                          {`${row.networkSpread.venues} ${row.networkSpread.venues === 1 ? 'venue' : 'venues'}`}
                        </button>
                        {` in ${row.networkSpread.windowDays} days`}
                      </div>
                    ) : null}
                    {!teamScoped && row.ringVenues ? (
                      <div className="risk-desk-network">
                        {'Card hit '}
                        <button type="button" className="risk-desk-linklike" onClick={() => openNetworkVenues(row)}>
                          {row.ringVenues.attributed
                            ? `${row.ringVenues.count} ${row.ringVenues.count === 1 ? 'venue' : 'venues'}`
                            : `${row.ringVenues.operators} ${row.ringVenues.operators === 1 ? 'operator' : 'operators'}`}
                        </button>
                        {row.ringVenues.suffix}
                        {row.ringVenues.names ? `: ${row.ringVenues.names}` : ''}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <Badge color={row.urgent ? 'error' : 'gray'}>{row.kindLabel}</Badge>
                  </td>
                  {!teamScoped ? (
                    <td>
                      <Badge color="gray">{row.serviceLevelLabel || 'Platform'}</Badge>
                    </td>
                  ) : null}
                  <td className="risk-desk-money">
                    <span className="risk-desk-face">{money.format(row.dollars)}</span>
                  </td>
                  <td className="risk-desk-money">
                    <span className="risk-desk-expected">{wholeMoney.format(row.expectedCost)}</span>
                    {row.likelihoodPct != null ? (
                      <div className="risk-desk-network">
                        {isCluster ? `Risk Model 1: ${row.likelihoodPct}% chance of fraud` : `${row.likelihoodPct}% modeled`}
                      </div>
                    ) : null}
                    {isCluster && row.domainFraud ? (
                      <>
                        <div className="risk-desk-network">
                          {row.domainFraud.pending ? 'Risk Model 2: pending' : `Risk Model 2: ${row.domainFraud.summaryLine}`}
                        </div>
                        {row.domainFraud.reasons ? (
                          <div className="risk-desk-network">{row.domainFraud.reasons}</div>
                        ) : null}
                      </>
                    ) : null}
                  </td>
                  <td>
                    {row.clock ? (
                      <span className={`risk-desk-clock${row.urgent ? ' risk-desk-clock-urgent' : ''}`}>{row.clock}</span>
                    ) : (
                      <span className="risk-desk-clock-empty">—</span>
                    )}
                  </td>
                  <td className="risk-desk-right risk-desk-nowrap">
                    {row.type === 'review' ? (
                      confirming ? (
                        <div className="risk-desk-confirm">
                          <Checkbox label="Report as fraudulent" checked={fraudulent} onChange={(e) => setFraudulent(e.target.checked)} />
                          <div className="risk-desk-block-list">
                            <Checkbox label="Add buyer to block list" checked={blockList} onChange={(e) => setBlockList(e.target.checked)} />
                            <span className="risk-desk-hint" title="This blocks both the credit card and the email address." aria-label="This blocks both the credit card and the email address.">?</span>
                          </div>
                          <div className="risk-desk-confirm-buttons">
                            <Button variant="secondary" onClick={() => setConfirmKey(null)} disabled={busy}>Cancel</Button>
                            <Button variant="destructive" onClick={() => refund(row)} disabled={busy}>Confirm refund</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="risk-desk-actions">
                          {row.canApprove ? (
                            <Button variant="secondary" onClick={() => approve(row)} disabled={busy}>Approve</Button>
                          ) : null}
                          {row.canRefund ? (
                            <Button variant="destructive" onClick={() => openRefund(row)} disabled={busy}>Refund</Button>
                          ) : null}
                        </div>
                      )
                    ) : null}
                    {row.type === 'ring' && canAct ? (
                      <div className="risk-desk-actions">
                        {row.canBlock ? (
                          <Button variant="destructive" onClick={() => blockCard(row)} disabled={busy}>Block card</Button>
                        ) : null}
                        {row.canDismiss ? (
                          <Button variant="secondary" onClick={() => dismiss(row)} disabled={busy}>Dismiss</Button>
                        ) : null}
                      </div>
                    ) : null}
                    {row.type === 'cluster' && canAct ? (
                      <div className="risk-desk-actions">
                        {row.canBlock ? (
                          <Button variant="destructive" onClick={() => blockDomain(row)} disabled={busy}>Block domain</Button>
                        ) : null}
                        {row.canDismiss ? (
                          <Button variant="secondary" onClick={() => dismiss(row)} disabled={busy}>Dismiss</Button>
                        ) : null}
                      </div>
                    ) : null}
                    {row.type === 'dispute' && row.canSubmit && canAct ? (
                      <div className="risk-desk-actions">
                        <Button variant="primary" onClick={() => openDisputeReview(row)} disabled={busy}>Review &amp; submit</Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableWrap>
      )}
      {disputeReview ? (
        <DisputeEvidencePane
          isOpen
          disputeId={disputeReview.id}
          uploadTeamId={disputeReview.teamId}
          packetUrl={`${endpoints.dispute_packet}?id=${disputeReview.id}`}
          submitUrl={`${endpoints.submit_dispute}?id=${disputeReview.id}`}
          onClose={closeDisputeReview}
          onSubmitted={() => setDisputeSubmitted(true)}
        />
      ) : null}
      {pending ? (
        <DestructiveConfirmModal
          open
          onClose={() => setPending(null)}
          title={CONFIRMS[pending.kind].title}
          body={(
            <>
              {CONFIRMS[pending.kind].body(pending.row, teamScoped)}
              {pending.kind === 'dismiss' && !teamScoped && pending.row.domainSiblings && pending.row.domainSiblings.allTeams > 0 ? (
                <DomainSweep
                  siblings={pending.row.domainSiblings}
                  checked={applyDomain}
                  scope={applyScope}
                  onToggle={setApplyDomain}
                  onScope={setApplyScope}
                />
              ) : null}
            </>
          )}
          confirmLabel={CONFIRMS[pending.kind].confirmLabel}
          cancelLabel="Cancel"
          icon={CONFIRMS[pending.kind].icon}
          onConfirm={runPending}
        />
      ) : null}
      {netVenues ? (
        <Modal open onClose={closeNetworkVenues} icon={false} width={520} title="Venues this card touched">
          {netVenues.loading ? (
            <p className="risk-desk-modal-note">Loading…</p>
          ) : netVenues.error ? (
            <p className="risk-desk-modal-error">{netVenues.error}</p>
          ) : netVenues.venues.length === 0 ? (
            <p className="risk-desk-modal-note">No venues found for this card.</p>
          ) : (
            <ul className="risk-desk-venue-list">
              {netVenues.venues.map((venue, i) => (
                <li key={i} className="risk-desk-venue">
                  <div className="risk-desk-venue-name">
                    {venue.name}
                    {venue.city || venue.state ? ` — ${[venue.city, venue.state].filter(Boolean).join(', ')}` : ''}
                  </div>
                  <div className="risk-desk-venue-meta">
                    {[venue.team, `${venue.events} ${venue.events === 1 ? 'event' : 'events'}`, venue.dateRange].filter(Boolean).join(' · ')}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      ) : null}
      {clusterOrders ? (
        <Modal open onClose={closeClusterOrders} icon={false} width={720} title={`Orders — ${clusterOrders.title}`}>
          {clusterOrders.loading ? (
            <p className="risk-desk-modal-note">Loading…</p>
          ) : clusterOrders.error ? (
            <p className="risk-desk-modal-error">{clusterOrders.error}</p>
          ) : clusterOrders.orders.length === 0 ? (
            <p className="risk-desk-modal-note">No orders found for this cluster.</p>
          ) : (
            <table className="risk-desk-cluster-orders">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Buyer</th>
                  <th>Email</th>
                  <th>Card</th>
                </tr>
              </thead>
              <tbody>
                {clusterOrders.orders.map((order) => (
                  <tr key={order.orderId}>
                    <td>{`#${order.orderId}`}</td>
                    <td>{order.name || '—'}</td>
                    <td>{order.email || '—'}</td>
                    <td>{order.card || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      ) : null}
      {orderModal ? (
        <Modal open onClose={() => setOrderModal(null)} icon={false} width={1040} title={`Order #${orderModal.orderId}`}>
          {orderLoading ? <p className="risk-desk-modal-note">Loading order…</p> : null}
          <iframe
            className="risk-desk-order-frame"
            title={`Order #${orderModal.orderId}`}
            src={`${orderModal.orderPath}?embedded=1`}
            onLoad={() => setOrderLoading(false)}
          />
        </Modal>
      ) : null}
    </>
  );
};

RiskDesk.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['review', 'dispute', 'ring', 'cluster']).isRequired,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    kind: PropTypes.string,
    kindLabel: PropTypes.string.isRequired,
    teamName: PropTypes.string,
    teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    subject: PropTypes.string,
    subtitle: PropTypes.string,
    domainSiblings: PropTypes.shape({
      sameTeam: PropTypes.number,
      allTeams: PropTypes.number,
    }),
    dollars: PropTypes.number,
    expectedCost: PropTypes.number,
    urgent: PropTypes.bool,
    clock: PropTypes.string,
    deadlineHours: PropTypes.number,
    daysQuiet: PropTypes.number,
    likelihoodPct: PropTypes.number,
    domainFraud: PropTypes.shape({
      pending: PropTypes.bool,
      score: PropTypes.number,
      riskLevel: PropTypes.string,
      riskLevelLabel: PropTypes.string,
      confidence: PropTypes.number,
      summaryLine: PropTypes.string,
      reasons: PropTypes.string,
    }),
    disputeReason: PropTypes.string,
    cardLabel: PropTypes.string,
    buyer: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
      orderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      orderPath: PropTypes.string,
    }),
    flagReason: PropTypes.string,
    networkSpread: PropTypes.shape({
      events: PropTypes.number,
      venues: PropTypes.number,
      windowDays: PropTypes.number,
    }),
    ringVenues: PropTypes.shape({
      count: PropTypes.number,
      operators: PropTypes.number,
      attributed: PropTypes.bool,
      suffix: PropTypes.string,
      names: PropTypes.string,
    }),
    canApprove: PropTypes.bool,
    canRefund: PropTypes.bool,
    canBlock: PropTypes.bool,
    canDismiss: PropTypes.bool,
    canSubmit: PropTypes.bool,
    serviceLevel: PropTypes.string,
    serviceLevelLabel: PropTypes.string,
  })),
  endpoints: PropTypes.shape({
    approve: PropTypes.string,
    refund: PropTypes.string,
    block_card: PropTypes.string.isRequired,
    block_domain: PropTypes.string.isRequired,
    dismiss: PropTypes.string.isRequired,
    dispute_packet: PropTypes.string,
    submit_dispute: PropTypes.string,
    network_venues: PropTypes.string,
    cluster_orders: PropTypes.string,
  }).isRequired,
  currencyCode: PropTypes.string,
  teamScoped: PropTypes.bool,
  canAct: PropTypes.bool,
  bare: PropTypes.bool,
};

RiskDesk.defaultProps = { rows: [], currencyCode: 'USD', teamScoped: false, canAct: true, bare: false };

export default RiskDesk;
